import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { MAX_DELTA_YEARS } from "../../../../classes/constants/validationConstants";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { ModalModalityEnum } from "../../../../classes/enums/ModalModalityEnum";
import { GENDER } from "../../../../classes/enums/PatientInfoEnum";
import { SnackbarType } from "../../../../classes/enums/SnackbarTypeEnum";
import {
  formatPhoneNumber,
  getDefaultCountryCode,
  INPUT_PATTERNS,
  isDateWithinMax,
  isDateWithinMin,
  normalizeDateInput,
  sanitizePatientName,
  validateEmail,
  validatePhone,
} from "../../../../classes/utils/ValidationUtils";
import Button from "../../../../components/button/Button";
import CloseButton from "../../../../components/closeButton/CloseButton";
import DatePickerComponent from "../../../../components/datepicker/Datepicker";
import RadioButtons from "../../../../components/radioButtons/RadioButtons";
import TextBox from "../../../../components/textBox/TextBox";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useFetchPatients } from "../../../../hooks/queries/useFetchPatients";
import { useGetDuplicatePatient } from "../../../../hooks/useDuplicatePatinentCheck";
import { useFormState } from "../../../../hooks/useFormState";
import { useRouteState } from "../../../../hooks/useRouteState";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { addPatient } from "../../../../services/PatientService";
import "./PersonalInformationEditModal.css";

const deepEqual = (a, b) => {
  const isEmpty = val => val === "" || val === null || val === undefined;
  if (isEmpty(a) && isEmpty(b)) return true;

  const isNumericLike = val =>
    (typeof val === "number" && !Number.isNaN(val)) ||
    (typeof val === "string" && /^\d+$/.test(val));
  if (isNumericLike(a) && isNumericLike(b)) {
    return Number(a) === Number(b);
  }

  if (a instanceof Date || b instanceof Date) {
    return (
      a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
    );
  }

  if (a === b) return true;

  const isObjA = a !== null && typeof a === "object";
  const isObjB = b !== null && typeof b === "object";
  if (!isObjA || !isObjB) return false;

  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const key of keys) {
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
};

const PersonalInformationEditModal = React.memo(
  ({
    onPersonalDataSave = undefined,
    modalType = ModalModalityEnum.Edit,
    initialData = {},
    onSave = undefined,
    dismissButton = true,
    canMerge = false,
    onDuplicatePatientChange = undefined,
  }) => {
    const { t } = useTranslation();
    const { hidePopup } = useAppContext();
    const { patientData, updatePatientData, setOperationPatientId } =
      usePatientContext();
    const { showSnackbar } = useSnackbar();
    const { params } = useRouteState();
    const culture = useSelector(state => state.i18n.culture);
    const { currentFullPatientList } = useFetchPatients();
    const duplicateCheck = useGetDuplicatePatient(currentFullPatientList);
    const patientId = params?.patientId || patientData?._id || initialData?._id;
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [duplicatePatient, setDuplicatePatient] = useState(null);
    const maxDate = useMemo(() => {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date;
    }, []);
    const minDate = useMemo(() => {
      const date = new Date();
      date.setFullYear(date.getFullYear() - MAX_DELTA_YEARS);
      return date;
    }, []);

    const genderOptions = useMemo(
      () => [
        { label: t("28"), value: GENDER.MALE },
        { label: t("29"), value: GENDER.FEMALE },
      ],
      [t]
    );

    const formInitialData = useMemo(() => {
      const baseData =
        modalType === ModalModalityEnum.Edit ? patientData : initialData;

      return {
        ...baseData,
        first_name: sanitizePatientName(baseData?.first_name),
        last_name: sanitizePatientName(baseData?.last_name),
        address: baseData?.address || {},
      };
    }, [patientData, initialData, modalType]);

    const validateFields = data => {
      const errors = {};
      if (!data.first_name) errors.first_name = "First name is required";
      if (!data.last_name) errors.last_name = "Last name is required";
      if (!data.birthday) errors.birthday = "Date of birth is required";
      if (!data.gender) errors.gender = "Gender is required";

      const parsedBirthday = normalizeDateInput(data.birthday);
      if (data.birthday && !parsedBirthday) {
        errors.birthday = "Please enter a valid date";
      } else if (parsedBirthday) {
        if (!isDateWithinMax(parsedBirthday, maxDate)) {
          errors.birthday = "Patient must be at least 18 years old";
        } else if (
          modalType === ModalModalityEnum.Create &&
          minDate &&
          !isDateWithinMin(parsedBirthday, minDate)
        ) {
          errors.birthday = "Date is out of the allowed range";
        }
      }

      if (data.email && !validateEmail(data.email)) {
        errors.email =
          "Invalid email address format. Please enter a valid email (e.g., example@domain.com).";
      }

      if (data.phone && !validatePhone(data.phone)) {
        errors.phone = "Invalid phone number format. Please enter a valid number.";
      }

      return errors;
    };

    const handleFormSubmit = async (formData, changedFields) => {
      try {
        switch (modalType) {
          case ModalModalityEnum.Edit:
            if (!patientId) {
              return;
            }
            await updatePatientData(changedFields);
            break;
          case ModalModalityEnum.Create:
            if (isDuplicate) {
              setOperationPatientId(duplicatePatient._id);
            } else {
              const newPatientId = await addPatient(formData);
              setOperationPatientId(newPatientId);
            }
            break;
          default:
            return;
        }

        if (onPersonalDataSave) {
          onPersonalDataSave();
        }

        if (onSave) {
          onSave(formData);
        }
      } catch (error) {}
    };

    const {
      formData,
      errors,
      handleInputChange: baseHandleInputChange,
      handleDateChange,
      handleSubmit,
      setErrors,
    } = useFormState(formInitialData, handleFormSubmit, validateFields);

    const handleBlur = useCallback(
      e => {
        const { name } = e.target;
        const errorMsg = errors[name];
        if (errorMsg) {
          showSnackbar(SnackbarType.ERROR, errorMsg, { duration: 5000 });
        }
      },
      [errors, showSnackbar]
    );

    const handleSubmitWithFeedback = useCallback(
      async e => {
        const result = await handleSubmit(e);
        if (result === false) {
          showSnackbar(
            SnackbarType.ERROR,
            "Please review the highlighted fields and try again",
            { duration: 5000 }
          );
        }
      },
      [handleSubmit, showSnackbar]
    );

    const hasChanges = useMemo(
      () => !deepEqual(formData, formInitialData),
      [formData, formInitialData]
    );

    const defaultCountryCode = useMemo(() => getDefaultCountryCode(), []);

    const [validationPulse, setValidationPulse] = useState({});

    const handleValidationError = useCallback(
      (fieldName, errorMessage, options = {}) => {
        const isTransient = options?.transient;

        if (isTransient) {
          if (errorMessage) {
            setValidationPulse(prev => ({
              ...prev,
              [fieldName]: (prev[fieldName] || 0) + 1,
            }));
            showSnackbar(SnackbarType.ERROR, errorMessage, { duration: 5000 });
          }
          return;
        }

        setErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          const prevMessage = prevErrors[fieldName];
          if (errorMessage) {
            newErrors[fieldName] = errorMessage;
            if (errorMessage !== prevMessage) {
              showSnackbar(SnackbarType.ERROR, errorMessage, {
                duration: 5000,
              });
            }
          } else {
            delete newErrors[fieldName];
          }
          return newErrors;
        });
      },
      [setErrors, showSnackbar]
    );

    const handleInputChange = useCallback(
      e => {
        const { name, value } = e.target;

        if (name === "phone") {
          const formatted = formatPhoneNumber(value, defaultCountryCode);
          const syntheticEvent = {
            target: {
              name: "phone",
              value: formatted,
            },
          };
          baseHandleInputChange(syntheticEvent);

          setErrors(prevErrors => {
            const newErrors = { ...prevErrors };
            if (formatted && !validatePhone(formatted)) {
              newErrors.phone =
                "Invalid phone number format. Please enter a valid number.";
            } else {
              delete newErrors.phone;
            }
            return newErrors;
          });
        } else {
          baseHandleInputChange(e);

          if (name === "email") {
            setErrors(prevErrors => {
              const newErrors = { ...prevErrors };
              if (value && !validateEmail(value)) {
                newErrors.email =
                  "Invalid email address format. Please enter a valid email (e.g., example@domain.com).";
              } else {
                delete newErrors.email;
              }
              return newErrors;
            });
          }
        }
      },
      [baseHandleInputChange, setErrors, defaultCountryCode]
    );

    const isFormValid = useMemo(() => {
      if (modalType === ModalModalityEnum.Edit) {
        return !!(
          formData.first_name &&
          formData.last_name &&
          formData.birthday &&
          formData.gender &&
          !errors.email &&
          !errors.phone
        );
      } else {
        return !!(
          formData.first_name &&
          formData.last_name &&
          formData.birthday &&
          formData.gender &&
          !errors.email &&
          !errors.phone
        );
      }
    }, [formData, modalType, errors.email, errors.phone]);

    const handleDateChangeInPatientForm = timestamp => {
      handleDateChange("birthday", timestamp);
    };

    useEffect(() => {
      const allRequiredFilled =
        formData.first_name &&
        formData.last_name &&
        formData.birthday &&
        formData.gender;
      if (!allRequiredFilled) {
        setIsDuplicate(false);
        setDuplicatePatient(null);
        if (onDuplicatePatientChange) {
          onDuplicatePatientChange(null);
        }
        return;
      }

      const duplicate = duplicateCheck({
        first_name: formData.first_name,
        last_name: formData.last_name,
        birthday: formData.birthday,
        gender: formData.gender,
      });

      const isDuplicatePatient =
        !!duplicate &&
        (modalType === ModalModalityEnum.Create || duplicate._id !== patientId);

      setDuplicatePatient(duplicate);
      setIsDuplicate(isDuplicatePatient);
      if (onDuplicatePatientChange) {
        onDuplicatePatientChange(isDuplicatePatient ? duplicate : null);
      }
    }, [
      duplicateCheck,
      formData.first_name,
      formData.last_name,
      formData.birthday,
      formData.gender,
      modalType,
      patientId,
      onDuplicatePatientChange,
    ]);

    const renderEditFields = () => (
      <>
        <div className="form-row">
          <TextBox
            placeholder={t("6")}
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleInputChange}
            error={errors.first_name}
            allowedPattern={INPUT_PATTERNS.PATIENT_NAME}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.first_name || 0}
          />
          <TextBox
            placeholder={t("7")}
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleInputChange}
            error={errors.last_name}
            type="text"
            allowedPattern={INPUT_PATTERNS.PATIENT_NAME}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.last_name || 0}
          />
        </div>
        <div className="form-row">
          <DatePickerComponent
            name="birthday"
            handleChange={handleDateChangeInPatientForm}
            timestamp={formData.birthday}
            placeholderText={t("8")}
            locale={culture}
            showPlaceholder={false}
            error={errors.birthday}
            maxDate={maxDate}
            type="text"
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.birthday || 0}
          />
          <RadioButtons
            options={genderOptions}
            selectedOption={formData.gender}
            onChange={handleInputChange}
            name="gender"
            displayName={t("9")}
            error={errors.gender}
            type="text"
          />
        </div>
        <div className="form-row">
          <TextBox
            placeholder={t("84")}
            name="nationality"
            value={formData.nationality || ""}
            onChange={handleInputChange}
            type="text"
            allowedPattern={INPUT_PATTERNS.LETTERS}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.nationality || 0}
            error={errors.nationality}
          />
        </div>
        <div className="form-row">
          <TextBox
            placeholder={t("10")}
            name="address.street"
            value={formData.address?.street || ""}
            onChange={handleInputChange}
            type="text"
            allowedPattern={INPUT_PATTERNS.ALPHANUMERIC}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse["address.street"] || 0}
            error={errors["address.street"]}
          />
          <TextBox
            placeholder={t("11")}
            name="address.city"
            value={formData.address?.city || ""}
            onChange={handleInputChange}
            type="text"
            allowedPattern={INPUT_PATTERNS.LETTERS}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse["address.city"] || 0}
            error={errors["address.city"]}
          />
        </div>
        <div className="form-row">
          <TextBox
            placeholder={t("13")}
            name="address.city_code"
            value={formData.address?.city_code || ""}
            onChange={handleInputChange}
            allowedPattern={INPUT_PATTERNS.NUMBERS}
            maxLength={5}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse["address.city_code"] || 0}
            error={errors["address.city_code"]}
          />
          <TextBox
            placeholder={t("14")}
            name="address.country"
            value={formData.address?.country || ""}
            onChange={handleInputChange}
            allowedPattern={INPUT_PATTERNS.LETTERS}
            isSanitized={true}
            maxLength={50}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse["address.country"] || 0}
            error={errors["address.country"]}
          />
        </div>
        <div className="form-row">
          <TextBox
            placeholder={t("87")}
            name="email"
            value={formData.email || ""}
            onChange={handleInputChange}
            error={errors.email}
            allowedPattern={INPUT_PATTERNS.EMAIL}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.email || 0}
            onBlur={handleBlur}
          />
          <TextBox
            placeholder={t("88")}
            name="phone"
            value={formData.phone || ""}
            onChange={handleInputChange}
            error={errors.phone}
            maxLength={20}
            allowedPattern={INPUT_PATTERNS.PHONE}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.phone || 0}
            onBlur={handleBlur}
          />
        </div>
      </>
    );

    const renderCreateFields = () => (
      <>
        <div className="form-row">
          <TextBox
            placeholder={t("6") + "*"}
            name="first_name"
            value={formData.first_name || ""}
            onChange={handleInputChange}
            error={errors.first_name}
            type="text"
            allowedPattern={INPUT_PATTERNS.PATIENT_NAME}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.first_name || 0}
          />
          <TextBox
            placeholder={t("7") + "*"}
            name="last_name"
            value={formData.last_name || ""}
            onChange={handleInputChange}
            error={errors.last_name}
            type="text"
            allowedPattern={INPUT_PATTERNS.PATIENT_NAME}
            maxLength={50}
            isSanitized={true}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.last_name || 0}
          />
        </div>
        <div className="form-row">
          <DatePickerComponent
            name="birthday"
            handleChange={handleDateChangeInPatientForm}
            timestamp={formData.birthday}
            placeholderText={t("8") + " (dd/mm/yyyy)*"}
            locale={culture}
            showPlaceholder={true}
            error={errors.birthday}
            maxDate={maxDate}
            minDate={minDate}
            onValidationError={handleValidationError}
            validationPulseKey={validationPulse.birthday || 0}
          />
          <RadioButtons
            options={genderOptions}
            selectedOption={formData.gender}
            onChange={handleInputChange}
            name="gender"
            displayName={t("9")}
            error={errors.gender}
          />
        </div>
      </>
    );

    const getSubmitButtonText = () => {
      return modalType === ModalModalityEnum.Edit
        ? t("51")
        : isDuplicate && canMerge
        ? t("3")
        : t("64");
    };

    return (
      <div
        className={`personal-information-edit-modal ${
          modalType === ModalModalityEnum.Create ? "create-mode" : ""
        }`}
      >
        {modalType === ModalModalityEnum.Edit && dismissButton && (
          <CloseButton onClose={hidePopup} />
        )}
        {modalType === ModalModalityEnum.Edit
          ? renderEditFields()
          : renderCreateFields()}
        <div className="button-container">
          <Button
            value={getSubmitButtonText()}
            onClick={handleSubmitWithFeedback}
            disabled={
              !isFormValid ||
              (modalType === ModalModalityEnum.Edit && !hasChanges) ||
              (isDuplicate && !canMerge)
            }
          />
        </div>
      </div>
    );
  }
);

export default PersonalInformationEditModal;
